// src/components/Earnings.tsx

import { useState, useEffect } from "react";
import { useUser } from "@/context/UserContext";
import NFTStakeCard from "./StakeCard.tsx"
import "@/styles/Earnings.css";
import {
    contributeToPool,
    DAILY_REWARD_AMOUNT,
    getCurrentDateString,
    calculateDailyInventoryBananium,
    claimDailyInventoryBananium
} from "../../scripts/dailyRewards";
import { doc, onSnapshot, Timestamp, getDoc } from "firebase/firestore";
import { db } from "@/config/firebase";

interface EarningsPanelProps {
    onClose: () => void;
}

interface PoolInfo {
    totalContribution: number;
    contributorCount: number;
    rewardDistributed: boolean;
    cutoffTimestamp?: Timestamp;
}

interface LastPoolInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    poolData: PoolInfo | null;
    lastDayUserContribution: number;
    lastDayRewardAmount: number;
}

const LastPoolInfoModal: React.FC<LastPoolInfoModalProps> = ({ isOpen, onClose, poolData, lastDayUserContribution, lastDayRewardAmount }) => {
    if (!isOpen) return null;
    if (!poolData) return <p>No pool data available for the previous day.</p>;

    const userReward = poolData?.totalContribution > 0
        ? (lastDayUserContribution / poolData?.totalContribution) * lastDayRewardAmount
        : 0;

    return (
        <div className="earnings-modal-overlay">
            <div className="earnings-modal-container">
                <button className="close-button" onClick={onClose}>✖</button>
                <h3>Previous Day's Pool Info</h3>
                {poolData ? (
                    <>
                        <p>Total Contribution: {Number(poolData.totalContribution).toFixed(4)} Bananas</p>
                        <p>Number of Contributors: {poolData.contributorCount}</p>
                        <p>Reward Distributed: {poolData.rewardDistributed ? 'Yes' : 'No'}</p>
                        {poolData.cutoffTimestamp && (
                            <p>Cutoff Time: {poolData.cutoffTimestamp.toDate().toLocaleString()}</p>
                        )}
                        <p>Your Contribution: {Number(lastDayUserContribution).toFixed(4)} Bananas</p>
                        <p>Total Reward: {lastDayRewardAmount} $Buink Tokens</p>
                        <p>Your Reward: {Number(userReward).toFixed(4)} $Buink Tokens</p>
                    </>
                ) : (
                    <p>No pool data available for the previous day.</p>
                )}
            </div>
        </div>
    );
};

function Earnings({ onClose }: EarningsPanelProps) {
    const { user, voxelInventory } = useUser();
    const [isOpen, setIsOpen] = useState(true);
    const [tab, setTab] = useState<"pool" | "nft">("pool");

    // State for today's daily pool data
    const [totalPoolContribution, setTotalPoolContribution] = useState(0);
    const [contributorCount, setContributorCount] = useState(0);
    const [userTodayContribution, setUserTodayContribution] = useState(0);

    // State for contribution input
    const [contributionAmountInput, setContributionAmountInput] = useState("");
    const [isContributing, setIsContributing] = useState(false);
    const [contributionError, setContributionError] = useState<string | null>(null);
    const [contributionSuccess, setContributionSuccess] = useState<string | null>(null);

    // State for daily inventory claim
    const [dailyInventoryBananium, setDailyInventoryBananium] = useState(0);
    const [canClaimInventoryBananium, setCanClaimInventoryBananium] = useState(false);
    const [isClaimingInventoryBananium, setIsClaimingInventoryBananium] = useState(false);
    const [claimInventoryMessage, setClaimInventoryMessage] = useState<string | null>(null);
    const [lastClaimTimestamp, setLastClaimTimestamp] = useState<Timestamp | null>(null);

    // State for viewing last pool info
    const [isLastPoolInfoModalOpen, setIsLastPoolInfoModalOpen] = useState(false);
    const [lastDayPoolInfo, setLastDayPoolInfo] = useState<PoolInfo | null>(null);
    const [lastDayUserContribution, setLastDayUserContribution] = useState(0);
    const [lastDayRewardAmount] = useState(DAILY_REWARD_AMOUNT); // Assuming reward amount doesn't change
    const parsedContributionAmount = Number.parseInt(contributionAmountInput, 10);
    const isContributionAmountValid = Number.isInteger(parsedContributionAmount) && parsedContributionAmount > 0;

    const handleClose = () => {
        setIsOpen(false);
        onClose();
    };

    const openLastPoolInfoModal = () => {
        setIsLastPoolInfoModalOpen(true);
        fetchLastDayPoolInfo();
    };

    const closeLastPoolInfoModal = () => {
        setIsLastPoolInfoModalOpen(false);
        setLastDayPoolInfo(null);
        setLastDayUserContribution(0);
    };

    const fetchLastDayPoolInfo = async () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayString = `${yesterday.getFullYear()}-${(yesterday.getMonth() + 1).toString().padStart(2, '0')}-${yesterday.getDate().toString().padStart(2, '0')}`;
        const lastDayPoolRef = doc(db, 'dailyRewardPools', yesterdayString);
        const lastDayUserContributionRef = user?.uid ? doc(db, 'dailyRewardPools', yesterdayString, 'contributions', user.uid) : null;

        try {
            const poolDocSnap = await getDoc(lastDayPoolRef);
            if (poolDocSnap.exists()) {
                setLastDayPoolInfo(poolDocSnap.data() as PoolInfo);
            } else {
                setLastDayPoolInfo(null);
                console.log("No pool data available for yesterday.");
            }

            if (lastDayUserContributionRef) {
                const contributionDocSnap = await getDoc(lastDayUserContributionRef);
                setLastDayUserContribution(contributionDocSnap.exists() ? (contributionDocSnap.data() as { amount?: number }).amount || 0 : 0);
            } else {
                setLastDayUserContribution(0);
            }
        } catch (error) {
            console.error("Error fetching last day's pool info:", error);
            setLastDayPoolInfo(null);
            setLastDayUserContribution(0);
        }
    };

    useEffect(() => {
        const todayString = getCurrentDateString();
        const dailyPoolRef = doc(db, 'dailyRewardPools', todayString);

        const unsubscribePool = onSnapshot(dailyPoolRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data() as PoolInfo;
                setTotalPoolContribution(data.totalContribution || 0);
                setContributorCount(data.contributorCount || 0);
            } else {
                setTotalPoolContribution(0);
                setContributorCount(0);
            }
        }, (error) => {
            console.error("[Earnings] Error fetching daily pool data:", error);
            setContributionError("Failed to load pool data.");
        });

        let unsubscribeUserContribution: () => void = () => { };
        let unsubscribeUserDoc: () => void = () => { };

        if (user?.uid) {
            const userContributionRef = doc(db, `dailyRewardPools/${todayString}/contributions`, user.uid);
            unsubscribeUserContribution = onSnapshot(userContributionRef, (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data() as { amount: number };
                    setUserTodayContribution(data.amount || 0);
                } else {
                    setUserTodayContribution(0);
                }
            }, (error) => {
                console.error("[Earnings] Error fetching user daily contribution:", error);
            });

            const userDocRef = doc(db, 'users', user.uid);
            unsubscribeUserDoc = onSnapshot(userDocRef, (docSnap) => {
                if (docSnap.exists()) {
                    const userData = docSnap.data() as { lastBananiumClaimTimestamp?: Timestamp };
                    setLastClaimTimestamp(userData?.lastBananiumClaimTimestamp || null);
                } else {
                    setLastClaimTimestamp(null);
                }
            }, (error) => {
                console.error("[Earnings] Error fetching user document for claim timestamp:", error);
            });

        } else {
            setUserTodayContribution(0);
            setLastClaimTimestamp(null);
        }

        return () => {
            unsubscribePool();
            unsubscribeUserContribution();
            unsubscribeUserDoc();
        };
    }, [user?.uid]);

    useEffect(() => {
        const todayString = getCurrentDateString();
        const inventoryBananium = calculateDailyInventoryBananium(voxelInventory || {});
        setDailyInventoryBananium(inventoryBananium);

        let claimedToday = false;
        if (lastClaimTimestamp instanceof Timestamp) {
            const lastClaimDateString = new Date(lastClaimTimestamp.toDate()).toISOString().split('T')[0];
            if (lastClaimDateString === todayString) {
                claimedToday = true;
            }
        }
        setCanClaimInventoryBananium(!claimedToday && !!user?.uid && inventoryBananium > 0);

    }, [voxelInventory, lastClaimTimestamp, user?.uid]);

    const handleContribute = async () => {
        if (!user?.uid) {
            setContributionError("You must be logged in to contribute.");
            return;
        }
        const amount = Number.parseInt(contributionAmountInput, 10);
        if (!Number.isInteger(amount) || amount <= 0) {
            setContributionError("Please enter a valid positive whole number.");
            return;
        }
        if (user.bananium === undefined || user.bananium < amount) {
            setContributionError("Insufficient Bananium balance.");
            return;
        }

        setIsContributing(true);
        setContributionError(null);
        setContributionSuccess(null);
        setClaimInventoryMessage(null);

        try {
            await contributeToPool(amount);
            setContributionSuccess("Contribution successful!");
            setContributionAmountInput("");
        } catch (error: any) {
            setContributionError(error.message || "Failed to contribute.");
        } finally {
            setIsContributing(false);
        }
    };

    const handleClaimInventoryBananium = async () => {
        if (!user?.uid) {
            setClaimInventoryMessage("You must be logged in to claim.");
            return;
        }
        if (!canClaimInventoryBananium) {
            return;
        }

        setIsClaimingInventoryBananium(true);
        setClaimInventoryMessage(null);
        setContributionError(null);
        setContributionSuccess(null);

        try {
            const claimedAmount = await claimDailyInventoryBananium();
            setClaimInventoryMessage(`Claimed ${Number(claimedAmount.claimedAmount || 0).toFixed(4)} Bananium from inventory!`);
        } catch (error: any) {
            setClaimInventoryMessage(error.message || "Failed to claim Bananium.");
        } finally {
            setIsClaimingInventoryBananium(false);
        }
    };

    if (!isOpen) return null;

    const estimatedNextDayShare = totalPoolContribution > 0
        ? (userTodayContribution / totalPoolContribution) * DAILY_REWARD_AMOUNT
        : 0;

    return (
        <div className="earnings-overlay">
            <div className="earnings-container">
                <button className="close-button" onClick={handleClose}>✖</button>
                <h2>🏆 Your Earnings</h2>

                <div className="balance-section">
                    <div className="balance-card">
                        <h3>🎯 Points</h3>
                        <p>{user?.points ?? 'N/A'} pts</p>
                    </div>

                    <div className="balance-card">
                        <h3>🍌 Bananium</h3>
                        <p>{Number(user?.bananium || 0).toFixed(4)} bananas</p>
                    </div>

                    <div className="balance-card">
                        <h3>🪙 $Buink Tokens</h3>
                        <p>{Number(user?.token404 || 0).toFixed(4)} $Buink</p>
                    </div>
                </div>

                <div className="reward-pool-section">
                    <h3>🎁 Today's Reward Pool</h3>
                    <p>Reward: {DAILY_REWARD_AMOUNT} $Buink tokens</p>
                    <p>Total Bananium Contributed: {Number(totalPoolContribution).toFixed(4)}</p>
                    <p>Contributors Today: {contributorCount}</p>

                    <div className="contribution-section">
                        <h4>Contribute Bananium to Pool:</h4>
                        <input
                            type="number"
                            value={contributionAmountInput}
                            onChange={(e) => setContributionAmountInput(e.target.value)}
                            placeholder="Amount"
                            step="1"
                            min="1"
                            disabled={isContributing || !user?.uid}
                        />
                        <button onClick={handleContribute} disabled={isContributing || !user?.uid || !isContributionAmountValid || parsedContributionAmount > (user?.bananium ?? 0)}>
                            {isContributing ? 'Contributing...' : 'Contribute'}
                        </button>
                        {contributionError && <p className="message error-message">{contributionError}</p>}
                        {contributionSuccess && <p className="message success-message">{contributionSuccess}</p>}
                    </div>

                    <h4>Your Contribution Today:</h4>
                    <p className="share-amount">{Number(userTodayContribution).toFixed(4)} bananas</p>

                    <h4>Estimated Next Day Reward:</h4>
                    <p className="share-amount">{Number(estimatedNextDayShare).toFixed(4)} $Buink tokens</p>

                    <p className="explanation-text">
                        Your reward for the daily pool will be based on your Bananium contribution today relative to the total Bananium contributed by all users today. Rewards are distributed after the daily reset.
                    </p>

                    <button onClick={openLastPoolInfoModal} className="view-last-pool-info-button">
                        View Last Pool Info
                    </button>
                </div>

                <div className="tab-content">
                    <div className="tabs-header">
                        <button
                            className={tab === "pool" ? "active" : ""}
                            onClick={() => setTab("pool")}
                        >
                            🪙 Daily Pool & Inventory
                        </button>
                        <button
                            className={tab === "nft" ? "active" : ""}
                            onClick={() => setTab("nft")}
                        >
                            🔐 NFT Staking
                        </button>
                    </div>
                    {tab === "pool" ? (
                        <div className="daily-claim-section item-card">
                            <h3>Claim Daily Inventory Bananium</h3>
                            <p>Eligible Amount: {Number(dailyInventoryBananium).toFixed(4)} bananas</p>
                            <button
                                onClick={handleClaimInventoryBananium}
                                disabled={isClaimingInventoryBananium || !user?.uid || !canClaimInventoryBananium}
                            >
                                {isClaimingInventoryBananium ? 'Claiming...' : 'Claim Now'}
                            </button>
                            {claimInventoryMessage && <p className={claimInventoryMessage.includes('Claimed') ? 'message success-message' : 'message error-message'}>{claimInventoryMessage}</p>}
                            {!user?.uid && <p className="message error-message">Login to claim daily Bananium.</p>}
                            {user?.uid && dailyInventoryBananium <= 0 && !claimInventoryMessage && !isClaimingInventoryBananium && <p className="explanation-text">Mine Bananium to be eligible for daily claims.</p>}
                            {user?.uid && !canClaimInventoryBananium && dailyInventoryBananium > 0 && !claimInventoryMessage?.includes('Claimed') && !isClaimingInventoryBananium && <p className="explanation-text">Already claimed today. Check back tomorrow!</p>}
                        </div>
                    ) : (
                        <NFTStakeCard />
                    )}
                </div>



                <LastPoolInfoModal
                    isOpen={isLastPoolInfoModalOpen}
                    onClose={closeLastPoolInfoModal}
                    poolData={lastDayPoolInfo}
                    lastDayUserContribution={lastDayUserContribution}
                    lastDayRewardAmount={lastDayRewardAmount}
                />
            </div>
        </div>
    );
}

export default Earnings;
